import React from "react";
import { motion } from "framer-motion/dist/framer-motion";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";
import { ToastContainer } from "react-toastify";

import { showSuccessToast } from "@components/Common/SuccessToast";
import { showErrorToast } from "@components/Common/ErrorToast";

import PageTitle from "@components/Common/PageTitle";
import MessageInfo from "@components/Common/MessageInfo";
import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import { store as userStore } from "@components/VerticalLayout/store";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { fetchGlobal } from "../../../../api/hooks";
import { SETTING_SERVIO } from "@constants/index";
import { RootState } from "../../../../redux/store";

import { ServioModalWrapper } from "./ModalWrapper";
import { ModalContentWrapper } from "./ModalContentWrapper";
import { ShopRowData } from "../ScanTo/types";
import { resetServio, setServio } from "../../../../redux/servioSlice";

// Strip in-memory blob: URLs from a config tree before sending it to the
// server. The server resolves uploaded files by filename + req.files, so any
// `blob:` value left in the JSON is dead weight.
function stripBlobUrls<T>(value: T): T {
    if (typeof value === "string") {
        return (value.startsWith("blob:") ? "" : value) as T;
    }
    if (Array.isArray(value)) {
        return (value.map(stripBlobUrls) as unknown) as T;
    }
    if (value !== null && typeof value === "object") {
        const result: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(
            value as Record<string, unknown>
        )) {
            result[key] = stripBlobUrls(val);
        }
        return result as T;
    }
    return value;
}

// DynamicTable's arrayOfCustomColumns descriptors for the shop list. The
// titles are translated at render time via t(titleKey); the dataKey is the
// French JSON field name the backend always returns regardless of locale.
const SHOP_DATA_COLUMNS = [
    { titleKey: "Shop", dataKey: "Boutique", testid: "servio-shop-cell" },
    { titleKey: "Address", dataKey: "Adresse", testid: "servio-address-cell" },
    {
        titleKey: "Postal code",
        dataKey: "Code Postal",
        testid: "servio-postal-code-cell",
    },
    { titleKey: "City", dataKey: "Ville", testid: "servio-city-cell" },
    { titleKey: "Country", dataKey: "Pays", testid: "servio-country-cell" },
] as const;

// Sentinel returned by getShopId when no legacy id field is set on the row.
// Saving / fetching against this id would hit the tenant-wide shop-0 record,
// so callers treat it as "invalid" and short-circuit.
const INVALID_SHOP_ID = "0" as const;

// Shop id used in the DynamicTable list query when oneShop mode is OFF:
// the backend interprets shopId=0 as "tenant-wide listing — return every
// shop for this franchise". Shares the same wire value as INVALID_SHOP_ID
// but is conceptually distinct (query parameter, NOT a row sentinel) — see
// review-ESS-3792_v1.md for the rationale.
const TENANT_WIDE_SHOP_ID = 0 as const;

// DynamicTable rows expose any of these legacy id fields; we read them in order.
function getShopId(shop: ShopRowData | null | undefined): string {
    return (shop?.shopId ?? shop?.Id ?? shop?.uid ?? INVALID_SHOP_ID) as string;
}

export function Servio() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { oneShop, franchiseID, shopID } = useSnapshot(userStore);
    // Named distinctly from the per-row `shopId` derived via getShopId() in
    // the save / open handlers below — same identifier in both scopes used
    // to shadow the other. This one drives only the DynamicTable list URL.
    const tableShopId = oneShop ? shopID : TENANT_WIDE_SHOP_ID;
    const selectedAccount = useSelector(
        (state: RootState) => state.global.selectedAccount
    );
    // Snapshot of the Redux Servio slice; handleSave reads it to ship the
    // current tab state up to the server.
    const servioConfig = useSelector((state: RootState) => state.servio);
    // Read the language reactively from i18next so a locale change re-renders
    // the page (translated headers, custom column titles via t(), etc.) and
    // the new Accept-Language header reaches DynamicTable. localStorage would
    // freeze the language to whatever it was when the page first loaded.
    const i18nextLng = i18n.language || "fr";
    const uuidUser = localStorage.getItem("uuidUser") || "";

    const [
        isConfigurationModalOpened,
        setIsConfigurationModalOpened,
    ] = React.useState<boolean>(false);
    const [selectedShop, setSelectedShop] = React.useState<ShopRowData | null>(
        null
    );
    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const handleSave = async () => {
        const shopId = getShopId(selectedShop);
        // getShopId falls back to INVALID_SHOP_ID when none of the three
        // legacy id fields is present. Saving to that id silently
        // overwrites the tenant-wide row, so surface an error instead.
        if (shopId === INVALID_SHOP_ID) {
            showErrorToast(t("Invalid shop"));
            return;
        }

        setIsSaving(true);
        try {
            const url = `${process.env.REACT_APP_API_V2_URL}/scanTo_servio/upsert/${shopId}/${franchiseID}/${SETTING_SERVIO}`;

            // Same upsert API as Scan to Start — only the setting type differs.
            // The backend expects multipart/form-data with a "data" field, so
            // we ship the JSON inside FormData even though Servio has no
            // image uploads (no "images" / "deletedImages" fields needed).
            const formData = new FormData();
            formData.append(
                "data",
                JSON.stringify(stripBlobUrls(servioConfig))
            );

            await fetchGlobal<any>(url, {
                method: "POST",
                body: formData,
            });

            showSuccessToast(t("Registration successfully"));
            setIsConfigurationModalOpened(false);
            // Bump a counter (rather than set true) so consecutive saves
            // trigger a re-fetch in DynamicTable each time.
            setDataIsUpdated((v) => (typeof v === "number" ? v + 1 : 1));
        } catch (error) {
            showErrorToast(`${t("There's an error")}!`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenConfiguration = React.useCallback(
        async (shop: ShopRowData) => {
            const shopId = getShopId(shop);
            // Same guard as handleSave: don't fetch (and seed Redux from)
            // the shared tenant-wide row when the row has no recognizable id.
            if (shopId === INVALID_SHOP_ID) {
                showErrorToast(t("Invalid shop"));
                return;
            }

            setIsLoading(true);

            try {
                const url = `${process.env.REACT_APP_API_V2_URL}/scanTo_servio/${shopId}/${franchiseID}/${SETTING_SERVIO}`;
                const response = await fetchGlobal<any>(url);

                const data = response?.data || response;

                // Always reset Redux to slice defaults so the modal starts from
                // the canonical DEFAULT_CONFIG.
                dispatch(resetServio());

                if (data && !data.error) {
                    // Real saved config: overlay every section that came back
                    // onto the defaults via the generic setServio reducer
                    // (it merges only the keys that are actually present).
                    dispatch(
                        setServio({
                            langues: data.langues,
                            home: data.home,
                            commandes: data.commandes,
                            notifications: data.notifications,
                        })
                    );
                }

                setSelectedShop(shop);
                setIsConfigurationModalOpened(true);
            } catch (error) {
                showErrorToast(`${t("There's an error")}!`);
            } finally {
                setIsLoading(false);
            }
        },
        [franchiseID, dispatch, t]
    );

    const arrayOfCustomColumns: customColumnProps[] = React.useMemo(
        () => [
            ...SHOP_DATA_COLUMNS.map((col, i) => ({
                indexOFColumn: i + 1,
                columnName: t(col.titleKey),
                customJsx: (e: { selectedRow: ShopRowData }) => (
                    <span data-testid={col.testid}>
                        {e.selectedRow?.[col.dataKey] as React.ReactNode}
                    </span>
                ),
            })),
            {
                indexOFColumn: SHOP_DATA_COLUMNS.length + 1,
                columnName: t("Actions"),
                customJsx: (e: { selectedRow: ShopRowData }) => (
                    <div className="d-flex justify-content-center">
                        <CustomMainColorButton
                            data-testid="servio-configure-btn"
                            className="servio_btn_configure"
                            rounded
                            variant="primary"
                            disabled={isLoading}
                            onClick={() =>
                                handleOpenConfiguration(e.selectedRow)
                            }
                        >
                            {t("Configure")}
                        </CustomMainColorButton>
                    </div>
                ),
            },
        ],
        [t, isLoading, handleOpenConfiguration]
    );

    return (
        <React.Fragment>
            <PageTitle title={"Servio"} data-testid="servio-page-title" />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="page-content__clz home-page-content__clz"
                data-testid="servio-page-container"
            >
                <StyledBreadcrumb
                    data-testid="servio-breadcrumb"
                    items={[
                        {
                            item: "Settings".toUpperCase(),
                            link: `${process.env.REACT_APP_PORTAIL_URL}`,
                        },
                        {
                            item: selectedAccount?.toUpperCase() || "",
                            link: oneShop
                                ? "/setting/customer-account"
                                : "/compte",
                        },
                        {
                            item: t("Application").toUpperCase(),
                            link: "applications",
                        },
                        {
                            item: t("Scan to Start").toUpperCase(),
                            link: "/scan-to-start",
                        },
                        {
                            item: t("Servio").toUpperCase(),
                            link: "#",
                        },
                    ]}
                />
                <MessageInfo
                    data-testid="servio-message-info"
                    message={t(
                        "Configure the Servio application settings including languages, home page, orders and notifications."
                    )}
                />
                <DynamicTable
                    // Force a full remount when the locale changes so the
                    // table refetches with the new Accept-Language header
                    // (DynamicTable doesn't watch requestHeader updates).
                    key={i18nextLng}
                    data-testid="servio-shop-table"
                    requestHeader={{
                        "Accept-Language": i18nextLng,
                    }}
                    url={`${process.env.REACT_APP_API_V2_URL}/scanTo_servio?shopId=${tableShopId}&franchiseId=${franchiseID}&uuidUser=${uuidUser}&type=${SETTING_SERVIO}`}
                    canSelect
                    showGlobalFilter
                    showFilter
                    filterActive={filterActive}
                    setLocalFilterActive={setLocalFilterActive}
                    arrayOfCustomColumns={arrayOfCustomColumns}
                    setDataIsUpdated={setDataIsUpdated}
                    dataIsUpdated={dataIsUpdated}
                    minHeight="520px"
                    maxHeight="520px"
                />
            </motion.div>

            {isConfigurationModalOpened && (
                <ServioModalWrapper
                    isConfigurationModalOpened={isConfigurationModalOpened}
                    setIsConfigurationModalOpened={
                        setIsConfigurationModalOpened
                    }
                    shopName={
                        (selectedShop?.Boutique as string | undefined) ||
                        (selectedShop?.shopName as string | undefined) ||
                        ""
                    }
                    onSave={handleSave}
                    isLoading={isSaving}
                >
                    <ModalContentWrapper />
                </ServioModalWrapper>
            )}
            <ToastContainer limit={1} />
        </React.Fragment>
    );
}
